import React, { useEffect, useState, useCallback } from 'react';
import { PiLinkDuotone, PiCopyDuotone, PiTrashDuotone, PiCheckDuotone, PiEnvelopeDuotone, PiUserPlusDuotone } from 'react-icons/pi';
import { createInviteLink, getInviteLinks, revokeInviteLink } from '../../../api/inviteLinkService';
import { addMemberByEmail } from '../../../api/roomService';
import { publicUserName } from '../../utils/userDisplay';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Input from '../../common/Input';
import Badge from '../../common/Badge';

/**
 * Модал управления приглашениями в комнату.
 * Две секции: добавление по email + инвайт-ссылки.
 */
export default function InviteLinkModal({ isOpen, onClose, roomId }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [error, setError] = useState('');

  // --- Email invite ---
  const [email, setEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailError, setEmailError] = useState('');

  const loadLinks = useCallback(async () => {
    if (!roomId) return;
    setLoading(true);
    setError('');
    try {
      const data = await getInviteLinks(roomId);
      setLinks(Array.isArray(data) ? data : []);
    } catch {
      setError('Не удалось загрузить ссылки');
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    if (isOpen) {
      loadLinks();
      setEmail('');
      setEmailSuccess('');
      setEmailError('');
    }
  }, [isOpen, loadLinks]);

  const handleAddByEmail = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setEmailLoading(true);
    setEmailError('');
    setEmailSuccess('');
    try {
      const member = await addMemberByEmail(roomId, trimmed);
      setEmailSuccess(`${publicUserName(member)} добавлен в комнату`);
      setEmail('');
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;
      if (status === 404) {
        setEmailError('Пользователь с таким email не найден');
      } else if (status === 409) {
        setEmailError(msg || 'Пользователь уже в комнате');
      } else {
        setEmailError(msg || 'Не удалось добавить участника');
      }
    } finally {
      setEmailLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      const link = await createInviteLink(roomId, { expirationDays: 7 });
      setLinks((prev) => [link, ...prev]);
    } catch {
      setError('Не удалось создать ссылку');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (linkId) => {
    try {
      await revokeInviteLink(roomId, linkId);
      setLinks((prev) => prev.map((l) => (l.id === linkId ? { ...l, active: false } : l)));
    } catch {
      setError('Не удалось деактивировать ссылку');
    }
  };

  const buildUrl = (code) => `${window.location.origin}/invite/${code}`;

  const handleCopy = async (link) => {
    try {
      await navigator.clipboard.writeText(buildUrl(link.code));
      setCopiedId(link.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = buildUrl(link.code);
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(link.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const isExpired = (link) => new Date(link.expiresAt) < new Date();
  const isUsable = (link) => link.active && !isExpired(link) && (link.maxUses == null || link.usedCount < link.maxUses);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Приглашение в комнату" size="md">

      {/* ── Добавить по email ──────────────────────────────────────── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <PiEnvelopeDuotone size={16} style={{ color: 'var(--bm-text-muted)' }} />
          <span className="bm-text-sm" style={{ fontWeight: 600 }}>Добавить по email</span>
        </div>

        <form onSubmit={handleAddByEmail} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <Input
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={emailError}
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            size="md"
            icon={<PiUserPlusDuotone size={14} />}
            loading={emailLoading}
            disabled={!email.trim()}
          >
            Добавить
          </Button>
        </form>

        {emailSuccess && (
          <p className="bm-text-sm" style={{ color: 'var(--bm-accent-green)', marginTop: 6 }}>
            {emailSuccess}
          </p>
        )}
      </div>

      <div style={{ borderTop: '1px solid var(--bm-border)', paddingTop: 16 }}>
        {/* ── Инвайт-ссылки ──────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <PiLinkDuotone size={16} style={{ color: 'var(--bm-text-muted)' }} />
            <span className="bm-text-sm" style={{ fontWeight: 600 }}>Инвайт-ссылки</span>
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={<PiLinkDuotone size={14} />}
            onClick={handleCreate}
            loading={creating}
          >
            Создать ссылку
          </Button>
        </div>

        {error && <div className="bm-auth-error" style={{ marginBottom: 12 }}>{error}</div>}

        {loading ? (
          <p className="bm-text-secondary bm-text-sm">Загрузка...</p>
        ) : links.length === 0 ? (
          <p className="bm-text-secondary bm-text-sm" style={{ textAlign: 'center', padding: '16px 0' }}>
            Ссылок пока нет. Создайте первую!
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 280, overflowY: 'auto' }}>
            {links.map((link) => (
              <div
                key={link.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  borderRadius: 'var(--bm-radius)',
                  background: 'var(--bm-bg-secondary)',
                  border: '1px solid var(--bm-border)',
                }}
              >
                <PiLinkDuotone size={18} style={{ color: 'var(--bm-text-muted)', flexShrink: 0 }} />

                <div style={{ flex: 1, minWidth: 0 }}>
                  <code
                    className="bm-text-sm"
                    style={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {buildUrl(link.code)}
                  </code>
                  <div className="bm-text-secondary" style={{ fontSize: 11, marginTop: 2 }}>
                    Использовано: {link.usedCount}{link.maxUses != null ? `/${link.maxUses}` : ''}
                    {' · '}
                    {isExpired(link) ? 'Истекла' : `До ${new Date(link.expiresAt).toLocaleDateString()}`}
                  </div>
                </div>

                {isUsable(link) ? (
                  <Badge status="open" pulse>Активна</Badge>
                ) : (
                  <Badge status="closed">Неактивна</Badge>
                )}

                {isUsable(link) && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={copiedId === link.id ? <PiCheckDuotone size={14} /> : <PiCopyDuotone size={14} />}
                      onClick={() => handleCopy(link)}
                      title="Скопировать"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<PiTrashDuotone size={14} />}
                      onClick={() => handleRevoke(link.id)}
                      style={{ color: 'var(--bm-accent-red)' }}
                      title="Деактивировать"
                    />
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
